// The map editor's floating panel chrome: a positioned, collapsible card and its
// header. Extracted from MapEditorPage so panels can live in their own files —
// importing it back out of MapEditorPage would make those files circular.

import type { ReactNode } from 'react';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { Icon, type IconName } from './Icon';

export function HudCard({
  children,
  side = "left",
  sideOffset = 16,
  width = 320,
  collapsed,
  onToggleCollapse,
  collapseIcon = "chevron-down",
  collapsedTag,
  collapsedTitle,
  collapsedActions,
}: {
  children: ReactNode;
  side?: "left" | "right";
  sideOffset?: number;
  width?: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  collapseIcon?: IconName;
  collapsedTag?: string;
  collapsedTitle?: string;
  collapsedActions?: ReactNode;
}) {
  const { t } = useTranslation('map');
  if (collapsed && onToggleCollapse) {
    return (
      <div
        data-map-panel="true"
        data-map-print="hide"
        style={{
          position: "absolute",
          top: 16,
          [side]: sideOffset,
          background: COLORS.surfaceContainerLowest,
          borderRadius: 12,
          boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
          zIndex: 3,
          height: 44,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <button
          onClick={onToggleCollapse}
          title={t('hud.expand')}
          style={{
            border: "none",
            cursor: "pointer",
            padding: "0 8px 0 14px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "none",
            fontFamily: "inherit",
          }}
        >
          {collapsedTag && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: COLORS.outline,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              {collapsedTag}
            </span>
          )}
          {collapsedTag && collapsedTitle && (
            <span style={{ fontSize: 11, color: COLORS.outlineVariant }}>·</span>
          )}
          {collapsedTitle && (
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: COLORS.onSurface,
                whiteSpace: "nowrap",
              }}
            >
              {collapsedTitle}
            </span>
          )}
        </button>
        {collapsedActions && (
          <>
            <div style={{ width: 1, height: 24, background: COLORS.surfaceContainerHighest, flexShrink: 0 }} />
            {collapsedActions}
          </>
        )}
        <button
          onClick={onToggleCollapse}
          title={t('hud.expand')}
          style={{
            border: "none",
            cursor: "pointer",
            padding: "0 10px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            background: "none",
          }}
        >
          <Icon name="chevron-right" size={16} color={COLORS.outline} />
        </button>
      </div>
    );
  }
  return (
    <div
      data-map-panel="true"
      data-map-print="hide"
      style={{
        position: "absolute",
        top: 16,
        [side]: sideOffset,
        width,
        background: COLORS.surfaceContainerLowest,
        borderRadius: 12,
        boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
        overflow: "hidden",
        zIndex: 3,
        maxHeight: "calc(100% - 32px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {onToggleCollapse && (
        <button
          onClick={onToggleCollapse}
          title={t('hud.collapse')}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 28,
            height: 28,
            borderRadius: 6,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: COLORS.outline,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <Icon name={collapseIcon} size={16} />
        </button>
      )}
      {children}
    </div>
  );
}

export function HudHeader({ title, subtitle, accent }: { title: string; subtitle?: string; accent?: string }) {
  return (
    <div
      style={{
        padding: "14px 16px",
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        borderTop: accent ? `3px solid ${accent}` : undefined,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}
